import apiClient from '../Api/ApiClient';
import { SuggestedDocument } from '../types';

const STORAGE_KEY = 'docarn_suggestions';

class SuggestionService {
  getAll(): SuggestedDocument[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      console.error("Failed to parse suggestions from localStorage", e);
    }
    return [];
  }

  saveAll(items: SuggestedDocument[]) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items || []));
    } catch (e) {
      console.error("Failed to save suggestions to localStorage", e);
    }
  }

  async add(suggestion: Omit<SuggestedDocument, 'id' | 'status' | 'submittedAt'> & { contactEmail?: string }): Promise<SuggestedDocument> {
    try {
      const response = await apiClient.post<any>('/api/suggestions', {
        type: suggestion.type,
        nature: suggestion.nature,
        reference: suggestion.reference,
        title: suggestion.title,
        description: suggestion.description,
        contactEmail: suggestion.contactEmail
      });

      if (response && response.id) {
        const created: SuggestedDocument = {
          id: response.id,
          ticketNumber: response.ticketNumber,
          type: response.type,
          nature: response.nature,
          reference: response.reference,
          title: response.title,
          description: response.description || '',
          contactEmail: response.contactEmail,
          status: response.status || 'PENDING',
          adminComment: response.adminComment,
          submittedAt: response.submittedAt || new Date().toISOString()
        };
        const all = this.getAll();
        const updated = [created, ...all.filter(s => s.id !== created.id && s.ticketNumber !== created.ticketNumber)];
        this.saveAll(updated);
        return created;
      }
    } catch (err) {
      console.warn("API /api/suggestions offline or failed, falling back to local storage:", err);
    }

    // Fallback local
    const all = this.getAll();
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    const newId = `T-${randomNum}`;
    const created: SuggestedDocument = {
      ...suggestion,
      id: newId,
      ticketNumber: `DOC-${new Date().getFullYear()}-${randomNum}`,
      status: 'PENDING',
      submittedAt: new Date().toISOString()
    };
    const updated = [created, ...all];
    this.saveAll(updated);
    return created;
  }

  async getMySuggestions(): Promise<SuggestedDocument[]> {
    try {
      const items = await apiClient.get<any[]>('/api/suggestions/my');
      if (Array.isArray(items)) {
        const mapped: SuggestedDocument[] = items.map(s => ({
          id: s.id,
          ticketNumber: s.ticketNumber,
          type: s.type,
          nature: s.nature,
          reference: s.reference,
          title: s.title,
          description: s.description || '',
          contactEmail: s.contactEmail,
          status: s.status,
          adminComment: s.adminComment,
          submittedAt: s.submittedAt
        }));
        this.saveAll(mapped);
        return mapped;
      }
    } catch (err) {
      console.warn("Could not fetch /api/suggestions/my, using local storage:", err);
    }
    return this.getAll();
  }

  async getAllAdmin(status?: string, search?: string): Promise<{ items: SuggestedDocument[]; totalCount: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (status) queryParams.append('status', status);
      if (search) queryParams.append('search', search);

      const res = await apiClient.get<any>(`/api/suggestions?${queryParams.toString()}`);
      if (res && Array.isArray(res.items)) {
        const mapped: SuggestedDocument[] = res.items.map((s: any) => ({
          id: s.id,
          ticketNumber: s.ticketNumber,
          type: s.type,
          nature: s.nature,
          reference: s.reference,
          title: s.title,
          description: s.description || '',
          contactEmail: s.contactEmail,
          status: s.status,
          adminComment: s.adminComment,
          submittedAt: s.submittedAt
        }));
        return { items: mapped, totalCount: res.totalCount || mapped.length };
      }
    } catch (err) {
      console.warn("Could not fetch /api/suggestions from admin endpoint:", err);
    }
    const local = this.getAll();
    return { items: local, totalCount: local.length };
  }

  async approve(id: string, comment?: string): Promise<boolean> {
    try {
      await apiClient.post(`/api/suggestions/${id}/approve`, { comment });
    } catch (err) {
      console.warn(`Could not approve suggestion ${id} via API:`, err);
    }
    const all = this.getAll();
    const updated = all.map(s => (s.id === id || s.ticketNumber === id) ? { ...s, status: 'APPROVED' as const, adminComment: comment } : s);
    this.saveAll(updated);
    return true;
  }

  async reject(id: string, comment?: string): Promise<boolean> {
    try {
      await apiClient.post(`/api/suggestions/${id}/reject`, { comment });
    } catch (err) {
      console.warn(`Could not reject suggestion ${id} via API:`, err);
    }
    const all = this.getAll();
    const updated = all.map(s => (s.id === id || s.ticketNumber === id) ? { ...s, status: 'REJECTED' as const, adminComment: comment } : s);
    this.saveAll(updated);
    return true;
  }
}

export const suggestionService = new SuggestionService();